import { RoomPriceSqmData, Insight } from './PriceM2PerRoomChart.types'

export const formatPricePerSqm = (price: number): string => {
  return `CHF ${Math.round(price).toLocaleString()}/m²`
}

export const formatShortPricePerSqm = (price: number): string => {
  return price.toLocaleString() + '/m²'
}

export const processRoomData = (
  aggregations: any,
  _priceType: 'median' | 'average' = 'median'
): RoomPriceSqmData[] => {
  if (!aggregations?.by_category?.buckets) {
    return []
  }

  const processed: RoomPriceSqmData[] = []

  aggregations.by_category.buckets.forEach((categoryBucket: any) => {
    const category = categoryBucket.key

    categoryBucket.by_rooms?.buckets?.forEach((bucket: any) => {
      const rooms = bucket.key

      // Filter out outliers and invalid data
      if (rooms > 0 && rooms <= 10 && bucket.listing_count?.value > 2) {
        const dataPoint: any = {
          category,
          rooms,
          avgPrice: Math.round(bucket.avg_price?.value || 0),
          medianPrice: Math.round(
            bucket.median_price?.values?.['50.0'] ||
              bucket.median_price?.values?.['50'] ||
              0
          ),
          count: bucket.listing_count.value,
          stdDev: Math.round(bucket.price_stats?.std_deviation || 0),
          min: Math.round(bucket.price_stats?.min || 0),
          max: Math.round(bucket.price_stats?.max || 0),
        }

        // Add quantiles if available - check multiple possible formats
        if (bucket.quantiles?.values) {
          dataPoint.quantiles = {
            q10: Math.round(
              bucket.quantiles.values['10.0'] ||
                bucket.quantiles.values['10'] ||
                0
            ),
            q30: Math.round(
              bucket.quantiles.values['30.0'] ||
                bucket.quantiles.values['30'] ||
                0
            ),
            q50: Math.round(
              bucket.quantiles.values['50.0'] ||
                bucket.quantiles.values['50'] ||
                0
            ),
            q70: Math.round(
              bucket.quantiles.values['70.0'] ||
                bucket.quantiles.values['70'] ||
                0
            ),
            q90: Math.round(
              bucket.quantiles.values['90.0'] ||
                bucket.quantiles.values['90'] ||
                0
            ),
          }
        }

        processed.push(dataPoint)
      }
    })
  })

  return processed
}

export const calculateKPIs = (
  data: RoomPriceSqmData[],
  priceType: 'median' | 'average' = 'median'
) => {
  if (data.length === 0) {
    return {
      totalListings: 0,
      avgPricePerSqm: 0,
      avgRooms: 0,
      avgPricePerSqmPerRoom: 0,
    }
  }

  const totalListings = data.reduce((sum, d) => sum + d.count, 0)
  const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'
  const weightedPricePerSqm =
    data.reduce((sum, d) => sum + d[priceField] * d.count, 0) / totalListings
  const avgRooms =
    data.reduce((sum, d) => sum + d.rooms * d.count, 0) / totalListings
  const avgPricePerSqmPerRoom = weightedPricePerSqm / avgRooms

  return {
    totalListings,
    avgPricePerSqm: weightedPricePerSqm,
    avgRooms,
    avgPricePerSqmPerRoom,
  }
}

type TranslationFn = (key: string, options?: Record<string, unknown>) => string

const defaultT: TranslationFn = (key: string) => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

export const generateInsights = (
  data: RoomPriceSqmData[],
  priceType: 'median' | 'average' = 'median',
  t: TranslationFn = defaultT
): Insight[] => {
  const insights: Insight[] = []

  if (data.length === 0) {
    return insights
  }

  const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'

  // Best value analysis for price/m²
  const valueRatios = data.map((d) => ({
    ...d,
    pricePerSqmPerRoom: d[priceField] / d.rooms,
  }))

  const bestValue = valueRatios.reduce((best, current) =>
    current.pricePerSqmPerRoom < best.pricePerSqmPerRoom ? current : best
  )

  insights.push({
    title: t('charts.insights.bestValue'),
    description: t('charts.insights.descriptions.priceM2PerRoom.bestValue', {
      rooms: bestValue.rooms,
      category: bestValue.category.toLowerCase(),
      priceM2: formatPricePerSqm(bestValue.pricePerSqmPerRoom),
    }),
  })

  // Price/m² variation analysis
  const apartmentData = data.filter((d) => d.category === 'Apartment')
  const houseData = data.filter((d) => d.category === 'House')

  if (apartmentData.length > 0 && houseData.length > 0) {
    const avgApartmentPricePerSqm =
      apartmentData.reduce((sum, d) => sum + d[priceField] * d.count, 0) /
      apartmentData.reduce((sum, d) => sum + d.count, 0)
    const avgHousePricePerSqm =
      houseData.reduce((sum, d) => sum + d[priceField] * d.count, 0) /
      houseData.reduce((sum, d) => sum + d.count, 0)
    const priceDiff = (
      ((avgHousePricePerSqm - avgApartmentPricePerSqm) /
        avgApartmentPricePerSqm) *
      100
    ).toFixed(1)

    insights.push({
      title: t('charts.insights.propertyTypePremium'),
      description: t('charts.insights.descriptions.priceM2PerRoom.propertyTypePremium', {
        premium: Math.abs(parseFloat(priceDiff)).toFixed(1),
        comparison:
          parseFloat(priceDiff) > 0
            ? t('charts.common.higher') || 'higher'
            : t('charts.common.lower') || 'lower',
      }),
    })
  }

  // Market efficiency
  const totalListings = data.reduce((sum, d) => sum + d.count, 0)
  const popularConfigs = data.filter((d) => d.count > totalListings * 0.05)

  if (popularConfigs.length > 0) {
    const mostPopular = popularConfigs.reduce((max, current) =>
      current.count > max.count ? current : max
    )

    insights.push({
      title: t('charts.insights.marketSweetSpot'),
      description: t('charts.insights.descriptions.priceM2PerRoom.marketSweetSpot', {
        rooms: mostPopular.rooms,
        category: mostPopular.category.toLowerCase(),
        count: mostPopular.count.toLocaleString(),
        priceM2: formatPricePerSqm(mostPopular[priceField]),
      }),
    })
  }

  // Price/m² progression analysis
  const roomCategories = [...new Set(data.map((d) => d.category))]
  roomCategories.forEach((category) => {
    const categoryData = data
      .filter((d) => d.category === category)
      .sort((a, b) => a.rooms - b.rooms)
    if (categoryData.length >= 3) {
      const priceChanges = []
      for (let i = 1; i < categoryData.length; i++) {
        const change =
          ((categoryData[i][priceField] - categoryData[i - 1][priceField]) /
            categoryData[i - 1][priceField]) *
          100
        priceChanges.push(change)
      }
      const avgChange =
        priceChanges.reduce((a, b) => a + b, 0) / priceChanges.length

      if (Math.abs(avgChange) > 1) {
        insights.push({
          title: t('charts.insights.priceM2Trend', { category }),
          description: t('charts.insights.descriptions.priceM2PerRoom.priceM2Trend', {
            direction:
              avgChange > 0
                ? t('charts.common.increases') || 'increases'
                : t('charts.common.decreases') || 'decreases',
            change: Math.abs(avgChange).toFixed(1),
            category: category.toLowerCase(),
          }),
        })
      }
    }
  })

  return insights
}

export const calculateTrendLine = (
  data: RoomPriceSqmData[],
  priceType: 'median' | 'average' = 'median'
) => {
  if (data.length < 2) return null

  const priceField = priceType === 'median' ? 'medianPrice' : 'avgPrice'
  const n = data.length
  const sumX = data.reduce((sum, d) => sum + d.rooms, 0)
  const sumY = data.reduce((sum, d) => sum + d[priceField], 0)
  const sumXY = data.reduce((sum, d) => sum + d.rooms * d[priceField], 0)
  const sumX2 = data.reduce((sum, d) => sum + d.rooms * d.rooms, 0)

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX)
  const intercept = (sumY - slope * sumX) / n

  const minX = Math.min(...data.map((d) => d.rooms))
  const maxX = Math.max(...data.map((d) => d.rooms))

  return [
    [minX, slope * minX + intercept],
    [maxX, slope * maxX + intercept],
  ]
}

export const getChartColors = () => ({
  apartment: '#2563eb',
  house: '#10b981',
})
